/**
 * useSmsWebSocket - Real-time SMS updates via WebSocket
 *
 * Subscribes to the single connection owned by `SmsSocketProvider`; the
 * transport (auth, reconnect backoff, keepalive) lives there. This hook
 * is just the event fan-out, so mounting it in five places costs five
 * listeners rather than five sockets.
 *
 * Outside the provider — an isolated component test, say — it degrades
 * to "not connected" and never fires.
 *
 * Events:
 * - connected     — server hello, first frame after the handshake
 * - sms:incoming  — new incoming message
 * - sms:sent      — admin (or Vero, is_auto_reply=true) sent a message
 * - sms:status    — delivery status update
 * - sms:read      — a device marked a thread read
 * - vero:state    — Vero auto-reply paused/resumed/opted-out for a phone
 * - call:*        — voice events; call.active / call:handoff /
 *                   call:handoff_invite drive the cross-device hand-off
 * - followup:*    — post-call thank-you queued / sent / cancelled
 * - openhouse:*   — a visitor signed in (no contact details on the wire) /
 *                   the worker finished a packet, stats or follow-up job /
 *                   a visiting agent uploaded a business card (openhouse:card)
 * - appointment:new — a visitor asked for an appointment on /book-appointment
 */

import { useEffect, useLayoutEffect, useRef } from 'react';
import { useSmsSocket } from '@/app/providers/SmsSocketProvider';

export type WsEventType =
    | 'connected'
    | 'sms:incoming' | 'sms:sent' | 'sms:status' | 'sms:read'
    | 'vero:state'
    | 'call:incoming' | 'call:status' | 'call:recording' | 'call:transcript' | 'call:outgoing'
    | 'call.active' | 'call:handoff' | 'call:handoff_invite' | 'call:transfer'
    | 'followup:scheduled' | 'followup:sent' | 'followup:cancelled'
    | 'openhouse:signin' | 'openhouse:job' | 'openhouse:card'
    | 'appointment:new'
    | 'pong';

export interface WsEvent {
    event: WsEventType;
    data: Record<string, unknown>;
}

/** @deprecated Use WsEvent instead */
export type SmsEvent = WsEvent;

interface UseSmsWebSocketOptions {
    // SMS callbacks
    onIncoming?: (data: WsEvent['data']) => void;
    onSent?: (data: WsEvent['data']) => void;
    onStatus?: (data: WsEvent['data']) => void;
    onRead?: (data: WsEvent['data']) => void;
    // Vero auto-reply state (pause / resume / STOP) for one phone
    onVeroState?: (data: WsEvent['data']) => void;
    // Voice callbacks
    onCallIncoming?: (data: WsEvent['data']) => void;
    onCallStatus?: (data: WsEvent['data']) => void;
    onCallRecording?: (data: WsEvent['data']) => void;
    onCallTranscript?: (data: WsEvent['data']) => void;
    onCallOutgoing?: (data: WsEvent['data']) => void;
    // Cross-device hand-off (conference registry)
    onCallActive?: (data: WsEvent['data']) => void;
    onCallHandoff?: (data: WsEvent['data']) => void;
    onCallHandoffInvite?: (data: WsEvent['data']) => void;
    // Post-call thank-you outbox
    onFollowupScheduled?: (data: WsEvent['data']) => void;
    onFollowupSent?: (data: WsEvent['data']) => void;
    onFollowupCancelled?: (data: WsEvent['data']) => void;
    // Open house kiosk sign-ins + worker jobs (packet / stats / follow-up)
    onOpenHouseSignIn?: (data: WsEvent['data']) => void;
    onOpenHouseJob?: (data: WsEvent['data']) => void;
    // A visiting agent's business-card photo landed ({open_house_id, sign_in_id, uploaded_at})
    onOpenHouseCard?: (data: WsEvent['data']) => void;
    // A new appointment request ({id, name, purpose, preferred_date, preferred_window, listing_id})
    onAppointmentNew?: (data: WsEvent['data']) => void;
    enabled?: boolean;
}

const NO_SOCKET = {
    connected: false,
    gaveUp: false,
    authFailed: false,
    reconnect: () => { /* no provider mounted */ },
};

export function useSmsWebSocket({
    onIncoming,
    onSent,
    onStatus,
    onRead,
    onVeroState,
    onCallIncoming,
    onCallStatus,
    onCallRecording,
    onCallTranscript,
    onCallOutgoing,
    onCallActive,
    onCallHandoff,
    onCallHandoffInvite,
    onFollowupScheduled,
    onFollowupSent,
    onFollowupCancelled,
    onOpenHouseSignIn,
    onOpenHouseJob,
    onOpenHouseCard,
    onAppointmentNew,
    enabled = true,
}: UseSmsWebSocketOptions = {}) {
    const socket = useSmsSocket();

    // Held in a ref so a caller passing inline arrow functions doesn't
    // churn the subscription on every render.
    const callbacks = {
        onIncoming, onSent, onStatus, onRead, onVeroState,
        onCallIncoming, onCallStatus, onCallRecording, onCallTranscript, onCallOutgoing,
        onCallActive, onCallHandoff, onCallHandoffInvite,
        onFollowupScheduled, onFollowupSent, onFollowupCancelled,
        onOpenHouseSignIn, onOpenHouseJob, onOpenHouseCard,
        onAppointmentNew,
    };
    const callbackRefs = useRef(callbacks);
    // Refreshed in a layout effect, i.e. synchronously inside the commit.
    // The socket listener runs outside React, so a frame arriving right
    // after a non-discrete commit (while a passive effect would still be
    // pending) must already see this render's closures.
    useLayoutEffect(() => {
        callbackRefs.current = callbacks;
    });

    const subscribe = socket?.subscribe;

    useEffect(() => {
        if (!enabled || !subscribe) return;
        return subscribe((msg: WsEvent) => {
            const cb = callbackRefs.current;
            switch (msg.event) {
                // SMS events
                case 'sms:incoming': cb.onIncoming?.(msg.data); break;
                case 'sms:sent': cb.onSent?.(msg.data); break;
                case 'sms:status': cb.onStatus?.(msg.data); break;
                case 'sms:read': cb.onRead?.(msg.data); break;
                case 'vero:state': cb.onVeroState?.(msg.data); break;
                // Voice events
                case 'call:incoming': cb.onCallIncoming?.(msg.data); break;
                case 'call:status': cb.onCallStatus?.(msg.data); break;
                case 'call:recording': cb.onCallRecording?.(msg.data); break;
                case 'call:transcript': cb.onCallTranscript?.(msg.data); break;
                case 'call:outgoing': cb.onCallOutgoing?.(msg.data); break;
                // Hand-off registry
                case 'call.active': cb.onCallActive?.(msg.data); break;
                case 'call:handoff': cb.onCallHandoff?.(msg.data); break;
                case 'call:handoff_invite': cb.onCallHandoffInvite?.(msg.data); break;
                // Post-call follow-ups
                case 'followup:scheduled': cb.onFollowupScheduled?.(msg.data); break;
                case 'followup:sent': cb.onFollowupSent?.(msg.data); break;
                case 'followup:cancelled': cb.onFollowupCancelled?.(msg.data); break;
                // Open houses
                case 'openhouse:signin': cb.onOpenHouseSignIn?.(msg.data); break;
                case 'openhouse:job': cb.onOpenHouseJob?.(msg.data); break;
                case 'openhouse:card': cb.onOpenHouseCard?.(msg.data); break;
                // Appointment requests
                case 'appointment:new': cb.onAppointmentNew?.(msg.data); break;
            }
        });
    }, [enabled, subscribe]);

    const { connected, gaveUp, authFailed, reconnect } = socket ?? NO_SOCKET;
    return { connected, gaveUp, authFailed, reconnect };
}
